/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import {
	catchError,
	distinctUntilChanged,
	filter,
	last,
	map,
	share,
	startWith,
	switchMap,
	take,
	takeUntil,
	withLatestFrom,
} from 'rxjs/operators';
import { BehaviorSubject, combineLatest, defer, EMPTY, Observable, of, Subject } from 'rxjs';
import { ReportType } from '../../types/ReportType';
import { DefaultReportNames, IReportTypeMapValue, reportTypeMap } from '../../types/ReportTypeUtils';
import * as appStrings from '@touchplan/strings';
import { hasActiveLayoutControl, ReportFormControl } from '../../types/ReportFormControl';
import { TimeScaleUtils } from '../../types/TimeScaleUtils';
import { TimeScale } from '../../types/TimeScale';
import { Location, Plan, PlanAccessLevel, Role, TicketType } from '@touchplan/common';
import { CustomFieldDefinition } from 'src/app/app-data/models/CustomFieldDefinition';
import { SortBy } from '../../types/SortBy';
import { enableSortGroupsBy, enableWbsGroupingLevel, GroupBy } from '../../types/GroupBy';
import { BreakUpBy } from '../../types/BreakUpBy';
import { ReportsService } from '../../services/reports.service';
import { ReportsFormManager } from '../../services/reports-form-manager.service';
import { SortGroupsBy } from '../../types/SortGroupsBy';
import { pokeProjectPresence } from '@touchplan/firebase-common';
import {
	FbdbRequests,
	IFbdbRequestPlanProjectResults,
	IProjectReportsConfig,
	IProjectReportsFbdbRequestResults,
} from 'src/app/services/fbdb-requests.service';
import { MatDialog } from '@angular/material/dialog';
import { GenericDialogComponent } from 'src/app/common/generic-dialog/generic-dialog.component';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { IPpcFormatMapValue, ppcFormatLabelMap } from '../../types/PpcFormat';
import { CloudStorageService } from 'src/app/services/cloud-storage.service';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { ReportBlobber } from 'src/app/services/report-blobber.service';
import { convertArrayToString } from 'analytics';
import { subjectSubscribeWithoutComplete } from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import { reportFormSortByConfig } from '../reportsFormTypeConfig';
import { sortStrings } from '../../types/SortByUtils';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { DefaultLoggerService } from '@touchplan/logging';
import { ExternalLinkage } from '../../types/ExternalLinkage';
import { exportFormatTypeMap, IOrderedExportFormatType } from '../../types/ExportFormatTypeUtils';
import { p6SupportedFormats } from 'src/app/app-data/constants/p6-supported-formats';
import {
	ICollapsedSwimlaneDropdownOption,
	ISwimlaneDropdownOption,
	ReportsSwimlaneDataService,
} from '../../services/reports-swimlane-data.service';
import { filterDisabledDefinitions } from 'src/app/app-data/state/CustomFieldDefinitions';
import { fancyDatabase } from '@touchplan/client-firebase';
import { UploadOutputFormat } from 'src/app/shared/ui/material/tp-file-upload.component/tp-file-upload.component';

export enum ReportUIState {
	EditingOptions,
	RunningReport,
	ReadyToViewReport,
	ReadyToDownload,
}

const TIMEOUT_OVERRIDE = { timeoutOverride: 5 * 60 * 1000 };

@Component({
	selector: 'angular-reports-panel',
	templateUrl: './reports.component.html',
	styleUrls: ['./reports.component.scss'],
	providers: [ReportsService, ReportsFormManager, LoggerServiceFactory.provide('ReportsComponent')],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportsComponent implements OnInit, OnDestroy {
	// This default is only used the first time a user visits the Reports UI.
	// After that, each time the user chooses a Report Type from the dropdown,
	// the type is saved via the ReportsUserPrefsService and later restored the
	// next time the user displays the UI:
	public static defaultReportType = ReportType.WeeklyWorkPlan;

	@Input() public projectId = '';
	@Input() public planId = '';
	@Input() public userId = '';
	@Input({ required: true }) public plans!: Observable<Map<string, Plan>>;
	@Input({ required: true }) public roles!: Observable<Map<string, Role>>;
	@Input({ required: true }) public swimlanesDropdownData!: Observable<Map<string, ISwimlaneDropdownOption>>;
	@Input({ required: true }) public locations!: Observable<Map<string, Location>>;
	@Input({ required: true }) public customFieldDefinitions!: Observable<Map<string, CustomFieldDefinition>>;

	// coerce an input into an observable (directly into the reportsService)
	@Input() set isAdmin(admin: boolean) {
		this.reportsService.isAdmin$.next(!!admin);
	}

	@Output() public closing = new EventEmitter();

	// Consumer of @Input that allows to provide external value for Report Type select
	// that will be used after component's initialization. It's not used in formGroup
	private _externalReportType$ = new BehaviorSubject<ReportType | null>(null);

	public formGroup: UntypedFormGroup;
	public reportTypeOption$: Observable<Map<string, IReportTypeMapValue>>;
	public ppcFormatOptions$: Observable<Map<string, IPpcFormatMapValue>>;

	public activeControl$ = new BehaviorSubject<{ [key in ReportFormControl]: boolean }>(
		{} as { [key in ReportFormControl]: boolean }
	);
	public thereAreLayoutControl$ = new BehaviorSubject<boolean>(true);

	public customFieldDefinitionsSync: Map<string, CustomFieldDefinition>;

	public timeScaleOption$: Observable<Map<string, TimeScale>>;
	public numberOfWeekOption$: Observable<string[]>;
	public planOption$: Observable<Map<string, Plan>>;
	public archivedPlanOption$: Observable<Map<string, Plan>>;
	public planOptionForCsvReport$: Observable<Map<string, Plan>>;
	public locationOption$: Observable<Map<string, Location>>;
	public roleOption$: Observable<Map<string, Role>>;

	public groupByOption$: Observable<Map<string, GroupBy>>;
	public sortByOption$: Observable<Map<string, SortBy>>;
	public sortGroupsByOption$: Observable<Map<string, SortGroupsBy>>;
	public breakUpByOption$: Observable<Map<string, BreakUpBy>>;
	public ticketTypeOptions$: Observable<Map<string, TicketType>>;
	public externalLinkageOptions$: Observable<Map<string, ExternalLinkage>>;
	public exportFormatTypeOptions$: Observable<Map<string, IOrderedExportFormatType>>;
	public customFields$: Observable<Map<string, CustomFieldDefinition>>;
	public wbsLevelList$: Observable<number[]>;

	private readonly stateUISubject$: BehaviorSubject<ReportUIState>;
	public stateUI$: Observable<ReportUIState>;

	public reportProgress$: BehaviorSubject<number>;

	public strings = appStrings;
	public p6SupportedFormats = p6SupportedFormats;
	public UploadOutputFormat = UploadOutputFormat;

	// The footer's Run Report button is enabled or disabled depending on the validity of all the controls:
	public disableRunReportButton$: Observable<boolean>;
	// Use this Observable to determine whether to show the Swimlanes Form Control in "Reports" Section
	private isSwimLanesReportEnabled$ = this.featureFlagManager.getFlag$(
		DynamicFlagTypes.SWIMLANES_REPORT_ENABLED
	);

	private isTicketWbsEnabled$ = this.featureFlagManager.getFlag$(DynamicFlagTypes.WBS_CODE_FILTER_ENABLED);

	protected isPlanArchiveEnabled$ = this.featureFlagManager.getFlag$(DynamicFlagTypes.PLAN_ARCHIVE_ENABLED);

	private shutdownSubject$ = new Subject<void>();
	private cancelSubject$ = new Subject<void>();
	private rolesMap: Map<string, Role>;
	private lastRequestResponse?: IProjectReportsFbdbRequestResults;
	protected downloadURL = '';

	protected collapsedSwimlanesDropdownOptions$: Observable<Map<string, ICollapsedSwimlaneDropdownOption>>;

	constructor(
		private reportsService: ReportsService,
		private reportBlobber: ReportBlobber,
		private fbdbRequests: FbdbRequests,
		private cloudStorageService: CloudStorageService,
		private featureFlagManager: FeatureFlagManager,
		private dialog: MatDialog,
		private logger: DefaultLoggerService
	) {
		this.stateUISubject$ = new BehaviorSubject<ReportUIState>(ReportUIState.EditingOptions);
		this.stateUI$ = this.stateUISubject$.asObservable();
		this.reportProgress$ = new BehaviorSubject(0);
	}

	public ngOnInit(): void {
		// Determine what the initial report type should be:
		const initialReportType =
			this._externalReportType$?.value ??
			this.reportsService.getSavedReportType(ReportsComponent.defaultReportType);

		// Create the FormGroup:
		this.formGroup = this.reportsService.createFormGroup(initialReportType);

		// The footer's Run Report button is enabled or disabled depending on the validity of all the controls:
		this.disableRunReportButton$ = this.reportsService.anyControlIsBothInvalidAndDirty$();

		this.reportTypeOption$ = combineLatest([
			this.featureFlagManager.getFlag$(DynamicFlagTypes.TICKET_EXPORT_ENABLED),
			this.featureFlagManager.getFlag$(DynamicFlagTypes.CSV_REPORT_ENABLED),
			this.reportsService.isAdmin$,
		]).pipe(
			map(([ticketExportEnabled, csvReportEnabled, isAdmin]) => {
				const editSafeMap = new Map(reportTypeMap);
				if (!ticketExportEnabled || !isAdmin) {
					editSafeMap.delete(this.strings.REPORT_TYPE_LABEL_EXPORT_TICKETS);
				}
				if (!csvReportEnabled || !isAdmin) {
					editSafeMap.delete(this.strings.REPORT_TYPE_LABEL_EXPORT_TO_CSV);
				}

				return editSafeMap;
			}),
			takeUntil(this.shutdownSubject$)
		);

		this.collapsedSwimlanesDropdownOptions$ = defer(() =>
			combineLatest([
				this.swimlanesDropdownData,
				this.reportsService
					.valueChangeFor$(ReportFormControl.Plans)
					.pipe(startWith(this.formGroup.get(ReportFormControl.Plans)?.value || [])),
			])
		).pipe(
			map(
				([swimlaneDropdownOptionsList, selectedPlans]: [Map<string, ISwimlaneDropdownOption>, string[]]) => {
					// if there is selected plans then filter if not just return the entire list
					if (!!selectedPlans?.length) {
						const filteredSwimlaneDropdownOptionsList = new Map();
						for (const [dropdownOptionKey, dropdownOption] of swimlaneDropdownOptionsList) {
							if (selectedPlans.includes(dropdownOption.planId)) {
								filteredSwimlaneDropdownOptionsList.set(dropdownOptionKey, dropdownOption);
							}
						}
						return filteredSwimlaneDropdownOptionsList;
					}
					return swimlaneDropdownOptionsList;
				}
			),
			map((filteredSwimlaneDropdownOptionsList) => {
				// The static method is preferred because the data can be filtered by plan before collapsing it,
				// and it does not need to be injected.
				return ReportsSwimlaneDataService.collapseDropdownOptionsByLabel(filteredSwimlaneDropdownOptionsList);
			})
		);

		// Update the siwmlanes form control with swimlanes that are associated with the selected plans.
		this.collapsedSwimlanesDropdownOptions$
			.pipe(
				withLatestFrom(
					defer(() =>
						this.reportsService
							.valueChangeFor$(ReportFormControl.Swimlanes)
							.pipe(startWith(this.formGroup.get(ReportFormControl.Swimlanes)?.value || []))
					)
				),
				map(([collapsedSwimlanesDropdownOptions, currentSwimlanes]) =>
					(currentSwimlanes as string[]).filter(
						(swimlane) => !!collapsedSwimlanesDropdownOptions.get(swimlane)
					)
				),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((updatedSwimlanes) => {
				this.formGroup.patchValue({ [ReportFormControl.Swimlanes]: updatedSwimlanes }, { emitEvent: false });
			});

		this.reportsService.startFormatTypeListener(this.projectId);

		this.ppcFormatOptions$ = of(ppcFormatLabelMap);

		this.setCustomFieldsData();

		this.reportsService.startCachingIds(
			this.plans,
			this.locations,
			this.roles,
			this.customFieldDefinitions,
			this.swimlanesDropdownData.pipe(
				map((swimlaneDropdownOptions) =>
					ReportsSwimlaneDataService.collapseDropdownOptionsByLabel(swimlaneDropdownOptions)
				)
			),
			this.customFields$
		);

		this.initReportTypeHandling(initialReportType);

		this.roles.pipe(takeUntil(this.shutdownSubject$)).subscribe((roles) => {
			this.rolesMap = roles;
		});

		this.initializeDropdownOptions();

		this.initConditionalSortGroupsByEnabling();

		this.initConditionalCSVExportEnabling();

		this.GroupByChangeListener();

		analyticsEventsService.openedReportsPanel();

		this.stateUI$.pipe(takeUntil(this.shutdownSubject$)).subscribe((UIState) => {
			switch (UIState) {
				case ReportUIState.RunningReport:
				case ReportUIState.ReadyToViewReport:
				case ReportUIState.ReadyToDownload:
					this.formGroup.disable();
					break;
				default:
					this.formGroup.enable();
					// all form controls are now enabled; restore any controls that were previously disabled
					// based on the selected report type, ensuring that only relevant fields are accessible
					this.reportsService.updateFormValueForSelectedReportType(this.currentReportType);
					break;
			}
		});

		this.reportsService.activeControl$.pipe(takeUntil(this.shutdownSubject$)).subscribe(this.activeControl$);

		// recalculate layout controls
		this.activeControl$
			.pipe(map(hasActiveLayoutControl), takeUntil(this.shutdownSubject$))
			.subscribe(subjectSubscribeWithoutComplete(this.thereAreLayoutControl$));

		// Saved the custom-field definitions in "customFieldDefinitionsSync"
		this.customFieldDefinitionsSync = new Map<string, CustomFieldDefinition>();
		this.customFieldDefinitions.pipe(takeUntil(this.shutdownSubject$)).subscribe((cfMap) => {
			this.customFieldDefinitionsSync = cfMap ? cfMap : new Map<string, CustomFieldDefinition>();
		});

		// Watch for updates to the externalReportType$ Observable:
		this._externalReportType$.pipe(takeUntil(this.shutdownSubject$)).subscribe((extReportType) => {
			if (extReportType) {
				this.handleExternalReportType(extReportType);
			}
		});

		// live save the report options
		this.reportsService.valueChange$.pipe(takeUntil(this.shutdownSubject$)).subscribe((changedValues) => {
			for (const [name, value] of Object.entries(changedValues)) {
				this.reportsService.saveControlValue(this.currentReportType, name as ReportFormControl, value);
			}
		});
	}

	// Feed an input into an observable
	@Input() set externalReportType(reportType: ReportType) {
		this._externalReportType$.next(reportType);
	}

	public handleExternalReportType(extReportType: ReportType): void {
		if (extReportType) {
			this.reportsService.updateFormValueForSelectedReportType(extReportType);
		}
	}

	private initReportTypeHandling(initialReportType: ReportType) {
		this.reportsService.reportTypeFormControl?.valueChanges
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((rt) => {
				// Patch value when we open report type
				if (this.planId && (rt as ReportType) === ReportType.ExportToCSV) {
					this.formGroup.patchValue({ [ReportFormControl.CSVPlan]: this.planId });
				}
			});

		// We also need to respond to ReportType changes, in that when a new type is selected,
		// we need to load the "prefs" saved for that type's controls from Firebase and restore
		// them into the controls' FormControls.
		this.reportsService.initFormControlUpdateOnReportTypeChange();

		// Now that we have our Observable "listeners" in place, set the initial Report Type:
		this.reportsService.initializeReportType(initialReportType, ReportsComponent.defaultReportType);
	}

	public get currentReportType() {
		return this.formGroup
			? this.reportsService.reportTypeFormControl?.value
			: ReportsComponent.defaultReportType;
	}

	private initializeDropdownOptions() {
		// "Time scale" dropdown options' Map:
		this.timeScaleOption$ = of(TimeScaleUtils.labelToEnumMap);

		// "Sort Groups by", "Break up by" and "Group by" dropdown options' Maps:
		this.breakUpByOption$ = of(
			new Map([
				[appStrings.REPORT_SORTBY_OFF, BreakUpBy.Off],
				[appStrings.REPORT_SORTBY_ROLE, BreakUpBy.Role],
			])
		);

		this.groupByOption$ = combineLatest([this.isSwimLanesReportEnabled$, this.isTicketWbsEnabled$]).pipe(
			map(([isSwimLanesReportEnabled, isTicketWbsEnabled]) => {
				const groupByOptions = new Map([
					[appStrings.REPORT_SORTBY_OFF, GroupBy.Off],
					[appStrings.REPORT_SORTBY_LOCATION, GroupBy.Location],
				]);
				if (isSwimLanesReportEnabled) {
					groupByOptions.set(appStrings.REPORT_SORTBY_SWIMLANE, GroupBy.Swimlane);
				}
				if (isTicketWbsEnabled) {
					groupByOptions.set(appStrings.REPORT_SORTBY_WBS, GroupBy.Wbs);
				}
				return groupByOptions;
			})
		);

		//maximize chances that mat-extended-select works by having two distinct lists
		//...also to simplify comparisons
		const listWithLocation = new Map([
			[appStrings.UICONTROL_TEXT_START_DATE, SortGroupsBy.StartDate],
			[appStrings.REPORT_SORTBY_LOCATION, SortGroupsBy.Location],
		]);
		const listWithWbs = new Map([
			[appStrings.UICONTROL_TEXT_START_DATE, SortGroupsBy.StartDate],
			[appStrings.REPORT_SORTBY_WBS, SortGroupsBy.Wbs],
		]);
		const listWithSwimlanes = new Map([
			[appStrings.UICONTROL_TEXT_START_DATE, SortGroupsBy.StartDate],
			[appStrings.REPORT_SORTBY_SWIMLANE, SortGroupsBy.Swimlane],
		]);

		//dynamic now
		//can init like this
		// - recalc "second value" on change of groupBy value
		// - surely mat-extended-select will support a dynamic options list, right? right?
		this.sortGroupsByOption$ = defer(() =>
			combineLatest([
				this.isSwimLanesReportEnabled$,
				this.reportsService
					.valueChangeFor$(ReportFormControl.GroupBy)
					.pipe(startWith(this.formGroup.get(ReportFormControl.GroupBy)?.value)),
			]).pipe(
				map(([swimlaneFeatureFlagEnabled, groupByVal]) => {
					if (groupByVal === GroupBy.Wbs) {
						return listWithWbs;
					} else if (swimlaneFeatureFlagEnabled && groupByVal === GroupBy.Swimlane) {
						return listWithSwimlanes;
					} else {
						return listWithLocation;
					} //location can be the default
				}),
				distinctUntilChanged()
			)
		);

		// "Number of weeks" dropdown options:
		this.numberOfWeekOption$ = of(['1', '2', '3', '4', '5', '6']);

		// "Plans", "Roles" and "Locations" dropdowns' options:
		this.planOption$ = this.plans.pipe(
			map(
				(planMap) =>
					new Map(
						[...planMap].filter(
							([_, v]) => !v.view.deletedAt && v.view.planAccess?.accessLevel !== PlanAccessLevel.archived
						)
					)
			)
		);
		this.archivedPlanOption$ = this.plans.pipe(
			map(
				(planMap) =>
					new Map(
						[...planMap].filter(
							([_, v]) => !v.view.deletedAt && v.view.planAccess?.accessLevel === PlanAccessLevel.archived
						)
					)
			)
		);
		const allPlanOption$ = this.plans.pipe(
			map((planMap) => new Map([...planMap].filter(([_, v]) => !v.view.deletedAt)))
		);
		this.planOptionForCsvReport$ = this.isPlanArchiveEnabled$.pipe(
			switchMap((isPlanArchiveEnabled) => (isPlanArchiveEnabled ? allPlanOption$ : this.planOption$))
		);
		this.roleOption$ = this.roles;
		this.locationOption$ = this.locations;

		// Listen for changes on value changed on the form. Defer so we actually get the proper values.
		this.sortByOption$ = defer(() =>
			this.reportsService.valueChange$.pipe(
				filter((changes) => !!changes.reportType),
				startWith({ reportType: this.formGroup.get('reportType')?.value }),
				map((changes) => {
					const sortOptions: SortBy[] = reportFormSortByConfig[changes.reportType];
					return new Map(sortOptions?.map((sortOption) => [sortStrings[sortOption], sortOption]));
				})
			)
		);

		this.ticketTypeOptions$ = of(
			new Map([
				[appStrings.TICKET_TYPE_TASK, TicketType.Task],
				[appStrings.TICKET_TYPE_MILESTONE, TicketType.Milestone],
				[appStrings.TICKET_TYPE_CONSTRAINT_UNASSIGNED, TicketType.Constraint],
			])
		);
		this.externalLinkageOptions$ = of(
			new Map<string, ExternalLinkage>([
				[appStrings.REPORT_EXTERNAL_SCHEDULE_ALL_TICKETS, ExternalLinkage.AllTickets],
				[appStrings.REPORT_WITH_EXTERNAL_SCHEDULE_ID, ExternalLinkage.WithExternalScheduleID],
				[appStrings.REPORT_WITHOUT_EXTERNAL_SCHEDULE_ID, ExternalLinkage.WithoutExternalScheduleID],
			])
		);

		this.exportFormatTypeOptions$ = combineLatest([
			this.featureFlagManager.getFlag$(DynamicFlagTypes.OPC_EXPORT_FORMAT_ENABLED, this.projectId),
			this.featureFlagManager.getFlag$(DynamicFlagTypes.MSP_EXPORT_FORMAT_ENABLED, this.projectId),
		]).pipe(
			map(([isOPCExportFormatEnabled, isMSPExportEnabled]) => {
				const exportFormatTypes = new Map(exportFormatTypeMap);
				if (!isOPCExportFormatEnabled && exportFormatTypes.has(appStrings.REPORT_EXPORT_TICKETS_FORMAT_OPC)) {
					exportFormatTypes.delete(appStrings.REPORT_EXPORT_TICKETS_FORMAT_OPC);
				}
				if (!isMSPExportEnabled && exportFormatTypes.has(appStrings.REPORT_EXPORT_TICKETS_FORMAT_MSP)) {
					exportFormatTypes.delete(appStrings.REPORT_EXPORT_TICKETS_FORMAT_MSP);
				}
				return exportFormatTypes;
			})
		);

		this.wbsLevelList$ = of([1, 2, 3, 4]);
	}

	public compareExportFormatsForSorting(a: string, b: string): number {
		const aOrdinal = exportFormatTypeMap.get(a).sortingOrdinal;
		const bOrdinal = exportFormatTypeMap.get(b).sortingOrdinal;
		return aOrdinal - bOrdinal;
	}

	/**
	 * The "Sort groups by" dropdown should only be enabled when:
	 *   - "Group by" is visible
	 *   - "Sort groups by" is visible
	 *   - "Group by"s selection is "Location"
	 * This sets up enabling and disabling the "Sort groups by" accordingly.
	 */
	private initConditionalSortGroupsByEnabling() {
		this.reportsService.valueChange$
			.pipe(
				filter((obj) => obj && obj.hasOwnProperty(ReportFormControl.GroupBy)),
				map((x) => (x ? x[ReportFormControl.GroupBy] : x)),
				startWith(this.formGroup.get(ReportFormControl.GroupBy)),
				map((groupBy) => enableSortGroupsBy(groupBy)),
				filter((enable) => this.formGroup.get(ReportFormControl.SortGroupsBy)?.enabled !== enable),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((shouldEnable) => {
				const control = this.formGroup.get(ReportFormControl.SortGroupsBy);
				if (!control) {
					return;
				}

				shouldEnable ? control.enable({ emitEvent: false }) : control.disable({ emitEvent: false });
			});
	}
	private initConditionalCSVExportEnabling() {
		this.reportsService.valueChange$
			.pipe(
				filter((obj) => obj && obj.hasOwnProperty(ReportFormControl.CSVExportType)),
				map((obj) => obj[ReportFormControl.CSVExportType]),
				map((exportType) => exportType === 'project' || exportType === 'projectWithArchivedPlans'),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((shouldDisable) => {
				const control = this.formGroup.get(ReportFormControl.CSVPlan);
				if (!control) {
					return;
				}
				if (shouldDisable && !control.disabled) {
					control.disable();
				} else if (!shouldDisable && control.disabled) {
					control.enable();
				}
			});
	}

	public timeScaleMenuSorting(a: string, b: string): number {
		return TimeScaleUtils.getSortOrderForLabel(a) - TimeScaleUtils.getSortOrderForLabel(b);
	}

	public sortReportTypeByOrdinal = (key1: string, key2: string): number => {
		return this.sortByOrdinal(key1, key2, reportTypeMap, 'sortingOrdinal');
	};

	public sortPpcFormatByOrdinal = (key1: string, key2: string): number => {
		return this.sortByOrdinal(key1, key2, ppcFormatLabelMap, 'sortingOrdinal');
	};

	private sortByOrdinal(
		key1: string,
		key2: string,
		optionsMap: Map<string, any>,
		sortingOrdinalMapValueField: string
	): number {
		const ordinal1 = optionsMap.get(key1)[sortingOrdinalMapValueField];
		const ordinal2 = optionsMap.get(key2)[sortingOrdinalMapValueField];
		return ordinal1 - ordinal2;
	}

	public alphaSortButWithOffFirst(a: string, b: string): number {
		if (a === appStrings.REPORT_SORTBY_OFF) {
			return -1;
		}
		if (b === appStrings.REPORT_SORTBY_OFF) {
			return 1;
		}
		return a.localeCompare(b);
	}

	public runReport() {
		if (!this.formGroup.valid) {
			return;
		}

		const reportType = this.currentReportType;

		//grab a snapshot of the formValue before disabling it
		const formValue = this.formGroup.value;

		this.stateUISubject$.next(ReportUIState.RunningReport);

		// Update projectPresence
		pokeProjectPresence(this.projectId, this.userId, fancyDatabase);

		// Clean report progress
		this.reportProgress$.next(0);

		let currentReportsErrorsFiltered$: Observable<
			IProjectReportsFbdbRequestResults | IFbdbRequestPlanProjectResults
		>;

		// Report
		if (reportType === ReportType.ExportToCSV || reportType === ReportType.ExportTickets) {
			// handle report config
			const reportsConfig = this.reportsService.convertReportFormToFbdbConfig(
				formValue,
				reportType,
				this.customFieldDefinitionsSync,
				this.projectId,
				DefaultReportNames[reportType]
			);

			let requestResultsAndCsvUrl$: Observable<[IFbdbRequestPlanProjectResults, string]>;
			if (reportType === ReportType.ExportTickets) {
				requestResultsAndCsvUrl$ = this.requestTicketExport$(
					reportsConfig,
					this.formGroup.get(ReportFormControl.ExportFormatTemplate)
				).pipe(share());
			} else {
				const isProjectWithArchivedPlans =
					formValue[ReportFormControl.CSVExportType] === 'projectWithArchivedPlans';
				const isProjectOrProjectWithArchivedPlans =
					formValue[ReportFormControl.CSVExportType] === 'project' || isProjectWithArchivedPlans;
				const fbdbConfig = isProjectWithArchivedPlans ? TIMEOUT_OVERRIDE : {};
				requestResultsAndCsvUrl$ = (
					isProjectOrProjectWithArchivedPlans
						? this.cloudStorageService.requestProjectCsvURL(
								this.projectId,
								isProjectWithArchivedPlans || undefined,
								fbdbConfig
						  )
						: this.plans.pipe(
								map((plans) => plans.get(formValue[ReportFormControl.CSVPlan])),
								switchMap((plan) =>
									this.cloudStorageService.requestPlanCsvURL(
										this.projectId,
										plan.$id,
										plan?.view?.planAccess?.accessLevel === PlanAccessLevel.archived ? TIMEOUT_OVERRIDE : {}
									)
								)
						  )
				).pipe(share());
			}

			const urlExistsFiltered$ = requestResultsAndCsvUrl$.pipe(
				map(([_, url]) => url),
				filter((url) => url !== ''),
				distinctUntilChanged(),
				take(1)
			);

			//stateUI management
			urlExistsFiltered$
				.pipe(
					map(() => ReportUIState.ReadyToDownload), //if success, download
					catchError((err) => {
						this.logger.warn(err);
						return of(ReportUIState.EditingOptions);
					}), //if error, back to edit
					takeUntil(this.cancelSubject$),
					takeUntil(this.shutdownSubject$)
				)
				.subscribe(subjectSubscribeWithoutComplete(this.stateUISubject$));

			urlExistsFiltered$.pipe(takeUntil(this.cancelSubject$), takeUntil(this.shutdownSubject$)).subscribe({
				next: (url: string) => {
					this.downloadURL = url;
					analyticsEventsService.requestCsv(formValue[ReportFormControl.CSVExportType]);
				},
				error: (error) => {
					const errorMessage = error.message || error;
					this.logger.error('Report generation error', errorMessage);
					// Return the Reports UI to the Editing Options state:
					this.stateUISubject$.next(ReportUIState.EditingOptions);

					this.dialog.open(GenericDialogComponent, {
						data: {
							title: appStrings.REPORT_ERROR_TITLE,
							yesButtonText: appStrings.UICONTROL_TEXT_CLOSE,
							content: [errorMessage, '', appStrings.REPORT_ERROR_DESCRIPTION],
						},
						disableClose: true,
					});
				},
			});

			// Observable containing just the response updates:
			const requestResult$ = requestResultsAndCsvUrl$.pipe(map(([results, _]) => results));

			// Observable variant with errors filtered.
			// For use below, for CSV reports and all others, in updating the spinner.
			// We handle errors in a different observable (see above), so "swallow" them in this Observable.
			currentReportsErrorsFiltered$ = this.filterErrors(requestResult$);
		} else {
			// reportType !== ReportType.ExportToCSV

			// handle report title
			const ppcInclusiveReportName = reportType === ReportType.PPCReport ? formValue.ppcFormat : reportType;
			const reportTitle = DefaultReportNames[ppcInclusiveReportName];
			// handle report config
			const reportsConfig = this.reportsService.convertReportFormToFbdbConfig(
				formValue,
				ppcInclusiveReportName,
				this.customFieldDefinitionsSync,
				this.projectId,
				reportTitle
			);

			// check if the user has selected any archived plans
			const hasArchivedPlansSelected =
				(this.formGroup.get(ReportFormControl.ArchivedPlansDropDown)?.value || []).length > 0;
			// if the user has selected to include archived plans, we need to increase the timeout for the request
			const fbdbConfig = hasArchivedPlansSelected ? TIMEOUT_OVERRIDE : {};

			// Request
			// handles bundling for breakup by role
			const currentReports$ = (
				formValue[ReportFormControl.BreakUpBy] === BreakUpBy.Role
					? this.fbdbRequests.requestProjectReportBundle(
							this.breakUpRolesConfig(reportsConfig, this.rolesMap, reportTitle),
							this.projectId,
							fbdbConfig
					  )
					: this.fbdbRequests.requestReports(reportsConfig, fbdbConfig)
			).pipe(share());

			// Observable variant with errors filtered.  For use below; also for use in updating the spinner.
			// We handle errors in a different observable (see currentReports$), so "swallow" them in this Observable.
			// This prevents default unhandled error handling from displaying a red error flag.  See TP-4399.
			currentReportsErrorsFiltered$ = this.filterErrors(currentReports$);

			//report completed event (error's ignored and will result in immediate completion)
			const reportComplete$ = currentReportsErrorsFiltered$.pipe(
				last(), //hilariously, this will throw an error if the previous error catching observable caught the error
				catchError((_) => EMPTY)
			);

			reportComplete$
				.pipe(
					map(() => ReportUIState.ReadyToViewReport),
					takeUntil(this.cancelSubject$),
					takeUntil(this.shutdownSubject$)
				)
				.subscribe(subjectSubscribeWithoutComplete(this.stateUISubject$));

			reportComplete$
				.pipe(takeUntil(this.cancelSubject$), takeUntil(this.shutdownSubject$))
				.subscribe((final) => {
					this.lastRequestResponse = final as IProjectReportsFbdbRequestResults;
					//some casting silliness to maintain backwards compatibility with the old reports panel
					analyticsEventsService.generatedReport(
						reportType,
						final.requestId,
						(reportsConfig as IProjectReportsConfig).asOfDate || '',
						(reportsConfig as IProjectReportsConfig).upToDate || '',
						convertArrayToString(reportsConfig.roleIds || []),
						convertArrayToString((reportsConfig as IProjectReportsConfig).planIds || [])
					);
					if (hasArchivedPlansSelected) {
						analyticsEventsService.archivedReportRun(
							this.formGroup.get(ReportFormControl.Plans)?.value ?? [],
							this.formGroup.get(ReportFormControl.ArchivedPlansDropDown)?.value ?? []
						);
					}
				});

			currentReports$.pipe(takeUntil(this.cancelSubject$), takeUntil(this.shutdownSubject$)).subscribe({
				error: (error) => {
					const errorMessage = error.message || error;
					// Log the error to the browser's console:
					this.logger.error('Report generation error', errorMessage);

					// Return the Reports UI to the Editing Options state:
					this.stateUISubject$.next(ReportUIState.EditingOptions);

					// Display an error dialog for the user:
					this.dialog.open(GenericDialogComponent, {
						data: {
							title: appStrings.REPORT_ERROR_TITLE,
							yesButtonText: appStrings.UICONTROL_TEXT_CLOSE,
							content: [errorMessage, '', appStrings.REPORT_ERROR_DESCRIPTION],
						},
						disableClose: true,
					});
				},
			});
		}

		// Handle percentage of status completed
		currentReportsErrorsFiltered$
			.pipe(
				filter((res) => !!res.statusStep && !!res.statusMax),
				map((res) => {
					// handle percentage of status completed
					if (res.statusStep && res.statusMax) {
						// this.logger.log(`%c statusStep: ${res.statusStep},  statusMax: ${res.statusMax}`, 'background: #AAFFAA');
						return (res.statusStep / res.statusMax) * 100;
					}
				}),
				takeUntil(this.cancelSubject$),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(subjectSubscribeWithoutComplete(this.reportProgress$));
	}

	/**
	 * Applies "catchError" to the given Observable and "swallows" the error.
	 * Use with caution.
	 * Other handling of the "ob$" parameter should NOT ignore the error;
	 * this function is to be used for processing other Observable contents
	 * without having to worry about uncaught errors.
	 */
	private filterErrors<T>(ob$: Observable<T>): Observable<T> {
		return ob$.pipe(catchError(() => EMPTY));
	}

	private breakUpRolesConfig(config: IProjectReportsConfig, roles: Map<string, Role>, reportType: string) {
		const selectedRoles = this.formGroup.get(ReportFormControl.Roles)?.value;
		return this.fbdbRequests.addParamsToBreakUpByRoleProjectReportList(
			selectedRoles,
			roles,
			config,
			reportType
		);
	}

	private requestTicketExport$(reportsConfig: IProjectReportsConfig, templateControl: AbstractControl) {
		const p6TemplateControlVal = templateControl?.value as File;

		//if the control exists and it's not the previously uploaded state
		// when the File[] contains more than 1, always use the first
		if (templateControl && p6TemplateControlVal?.size !== 0) {
			// && p6TemplateControlVal?.webkitRelativePath)
			return this.cloudStorageService.uploadP6Template(this.projectId, p6TemplateControlVal).pipe(
				last(),
				switchMap(() => this.cloudStorageService.requestTicketExport(reportsConfig))
			);
		}
		//already uploaded, or not needed
		else {
			return this.cloudStorageService.requestTicketExport(reportsConfig);
		}
	}

	// Custom Fields to be displayed in the "Layout" section of the Reports UI
	// Only Custom Fields that are enabled for ticket form.
	private setCustomFieldsData(): void {
		this.customFields$ = this.customFieldDefinitions.pipe(
			map((allCustomFieldDef) => filterDisabledDefinitions(allCustomFieldDef)),
			map((customFieldDef) => {
				if (customFieldDef) {
					return new Map([...customFieldDef].sort((a, b) => a[1].view.order - b[1].view.order));
				}
			})
		);
	}

	private GroupByChangeListener() {
		// For whatever reason, this was previusly not working with the valueChangeFor$(ReportFormControl.GroupBy)
		// observable for the initial state of the control.
		// It was emitting, but the control was not being updated. This is a workaround, copied from the SortGroupsBy
		// option, it does the same thing but more manually.
		this.reportsService.valueChange$
			.pipe(
				filter((obj) => obj && obj.hasOwnProperty(ReportFormControl.GroupBy)),
				map((x) => (x ? x[ReportFormControl.GroupBy] : x)),
				startWith(this.formGroup.get(ReportFormControl.GroupBy)),
				map((groupBy) => enableWbsGroupingLevel(groupBy)),
				filter((enable) => this.formGroup.get(ReportFormControl.WbsGroupingLevel)?.enabled !== enable),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((shouldEnable) => {
				const control = this.formGroup.get(ReportFormControl.WbsGroupingLevel);
				if (!control) {
					return;
				}
				shouldEnable ? control.enable({ emitEvent: false }) : control.disable({ emitEvent: false });
			});
	}

	public async viewReport() {
		const cloudUrl = this.lastRequestResponse?.reportBodyBlob;
		const reportType = this.lastRequestResponse?.reportName;
		const htmlStr = await this.reportBlobber.getReport(cloudUrl, reportType, this.projectId);
		this.reportBlobber.generateAndOpenReport(htmlStr);
	}

	public downloadCsv() {
		window.open(this.downloadURL, '_blank');
	}

	public cancel() {
		this.cancelSubject$.next();
		this.stateUISubject$.next(ReportUIState.EditingOptions);
	}

	public clearSettings() {
		this.reportsService.resetAndSaveDefaultsForReportType(this.currentReportType);
	}

	public editSettings() {
		this.stateUISubject$.next(ReportUIState.EditingOptions);
		this.reportProgress$.next(0);
	}

	public close() {
		this.closing.emit('closing');
	}

	public onArchivedPlansMultiselectClosed() {
		const selectedArchivedPlans = this.formGroup.get(ReportFormControl.ArchivedPlansDropDown)?.value;
		if (selectedArchivedPlans?.length) {
			this.fbdbRequests
				.requestPlanNearline(selectedArchivedPlans, this.projectId)
				.pipe(takeUntil(this.shutdownSubject$))
				.subscribe();
		}
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}
}
